import {
  endpoints,
  type AnonymousResponse,
  type LoginResponse,
} from "@sunrise/backend-types";
import { PrivateApiClient, publicApi } from "@sunrise/http-client";
import { Nullable, isNil } from "@sunrise/utils";

export async function fetchRefreshAuthTokens(
  host: string,
  refreshToken: string,
): Promise<LoginResponse> {
  const url = new URL(endpoints.refreshToken(host));

  url.searchParams.set("refresh_token", refreshToken);

  // NOTE: It is SUPER important to use publicApi here. Else the automatic token refresh will kick in on the refresh endpoint itself.
  return (await publicApi.get<LoginResponse>(url.href)).data;
}

export async function invalidateTokens(
  privateApi: PrivateApiClient,
  host: string,
  type: "access_token_only" | "all",
): Promise<void> {
  const url =
    type === "access_token_only"
      ? endpoints.invalidateAccessToken?.(host)
      : endpoints.invalidateAllTokens?.(host);

  if (!url) {
    return;
  }

  await privateApi.get(url);
}

export async function anonymousLogin(
  host: Nullable<string>,
  deviceTypeName: string,
) {
  if (isNil(host)) throw new Error("api host is not defined");

  const { data: user } = await publicApi.post<AnonymousResponse>(
    endpoints.anonymous(host),
  );

  const { data: login } = await publicApi.get<LoginResponse>(
    endpoints.login.withUserId(host, user.id, deviceTypeName),
  );

  return login;
}

export async function authenticateWithToken(
  host: Nullable<string>,
  deviceTypeName: string,
  token: string,
) {
  if (isNil(host)) throw new Error("api host is not defined");

  const { data: login } = await publicApi.get<LoginResponse>(
    endpoints.login.withToken(host, token, deviceTypeName),
  );

  return login;
}
